/**
 * Plain-language labels + help text for the project Settings form
 * (UX-06). Keyed by the dotted settings path so a Field component can
 * look up its own copy and so validation errors (which reference the
 * same dotted paths — see shared/lib/projectValidation.ts) can be
 * matched back to a field.
 */

export interface SettingsFieldCopy {
  /** Dotted path into `ProjectSettings`, e.g. "retries.max_fix_attempts". */
  key: string;
  label: string;
  help: string;
}

export const SETTINGS_COPY: Record<string, SettingsFieldCopy> = {
  'stack.backend': {
    key: 'stack.backend',
    label: 'Backend',
    help: 'Language of your API services',
  },
  'stack.web': {
    key: 'stack.web',
    label: 'Web',
    help: 'Front-end framework',
  },
  'stack.mobile': {
    key: 'stack.mobile',
    label: 'Mobile',
    help: 'Leave as None if unused',
  },
  'retries.max_fix_attempts': {
    key: 'retries.max_fix_attempts',
    label: 'Fix attempts per story',
    help: 'Tries before a human is asked (0–10)',
  },
  'retries.max_partial_retries': {
    key: 'retries.max_partial_retries',
    label: 'Partial-pass retries',
    help: 'Extra tries when only some tests pass (0–5)',
  },
  'review.enabled': {
    key: 'review.enabled',
    label: 'Automated code review',
    help: 'Run the reviewer agent on every story',
  },
  'review.auto_retry_on': {
    key: 'review.auto_retry_on',
    label: 'When review halts',
    help: 'What happens after the reviewer agent flags a story',
  },
  'limits.qa_test_floor': {
    key: 'limits.qa_test_floor',
    label: 'Min. tests per story',
    help: 'QA fails a story that adds fewer tests',
  },
  'limits.qa_test_ceiling': {
    key: 'limits.qa_test_ceiling',
    label: 'Max. tests per story',
    help: 'Caps test generation to control cost',
  },
  'limits.max_concurrent_stories': {
    key: 'limits.max_concurrent_stories',
    label: 'Stories in parallel',
    help: 'Higher is faster and costs more (1–20)',
  },
  'limits.max_iterations': {
    key: 'limits.max_iterations',
    label: 'Max agent iterations',
    help: 'Hard stop per story (1–200)',
  },
  auto_approve: {
    key: 'auto_approve',
    label: 'Auto-approve plans',
    help: 'Skip the plan review step for new epics',
  },
};

/** Friendly labels for the `review.auto_retry_on` enum. */
export const REVIEW_AUTO_RETRY_LABELS: Record<'none' | 'halt' | 'halt-and-advisory', string> = {
  none: 'Stop and ask me',
  halt: 'Retry automatically',
  'halt-and-advisory': 'Retry on halts and advisories',
};

/**
 * Find a validation error message that names `prefix` (a dotted settings
 * path or a bare top-level key such as "auto_approve"). Used to surface
 * shared/lib validation errors next to the field they describe instead of
 * only in the top summary. Returns the first match, or undefined.
 */
export function fieldError(errors: string[], prefix: string): string | undefined {
  return errors.find((e) => e === prefix || e.startsWith(`${prefix} `) || e.startsWith(`${prefix}:`));
}
